'use client';

import type { FlagCatalogEntry } from '@/@feature-flags/catalog';
import {
    KNOWN_FLAGS_PAGE_SIZE,
    filterFlagCatalog,
    pageFlagCatalog,
} from '@/@feature-flags/catalog';
import {
    RowActions,
    RowButton,
    STAFF_TABLE,
    StaffToolbar,
} from '@/components/staff-tools/staff-table';
import {
    Chip,
    Pagination,
    Table,
    TableBody,
    TableCell,
    TableColumn,
    TableHeader,
    TableRow,
} from '@heroui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAlignSectionOnChange } from './scroll-section';

const ENFORCED_LABEL: Record<FlagCatalogEntry['enforcedIn'], string> = {
    ui: 'App',
    api: 'API',
    both: 'App and API',
};

interface KnownFlagsPanelProps {
    entries: FlagCatalogEntry[];
    createdKeys: Set<string> | null;
    isLoadingKeys: boolean;
    isKeysError: boolean;
    onCreate: (entry: FlagCatalogEntry) => void;
}

export function KnownFlagsPanel({
    entries,
    createdKeys,
    isLoadingKeys,
    isKeysError,
    onCreate,
}: KnownFlagsPanelProps) {
    const [query, setQuery] = useState('');
    const [page, setPage] = useState(1);
    const sectionRef = useRef<HTMLElement>(null);
    useAlignSectionOnChange(sectionRef, `${page}:${query}`);

    const filtered = useMemo(() => filterFlagCatalog(entries, query), [entries, query]);
    const paged = useMemo(
        () => pageFlagCatalog(filtered, page, KNOWN_FLAGS_PAGE_SIZE),
        [filtered, page],
    );

    useEffect(() => {
        if (paged.page !== page) setPage(paged.page);
    }, [paged.page, page]);

    return (
        <section ref={sectionRef} aria-label="Catalog" className="flex flex-col gap-3">
            <StaffToolbar
                search={{
                    value: query,
                    onChange: (value) => {
                        setQuery(value);
                        setPage(1);
                    },
                    placeholder: 'Search keys or descriptions',
                }}
                filters={
                    <p className="text-small text-foreground-600">
                        Keys the product reads, and what turning each one on does.
                    </p>
                }
                count={
                    paged.total > 0
                        ? `${paged.rangeStart}–${paged.rangeEnd} of ${paged.total}`
                        : null
                }
            />

            {isKeysError ? (
                <p className="text-small text-danger" role="alert">
                    Could not check which flags already exist in this environment.
                </p>
            ) : null}

            <Table aria-label="Known flags" classNames={STAFF_TABLE}>
                <TableHeader>
                    <TableColumn>Flag</TableColumn>
                    <TableColumn>Read by</TableColumn>
                    <TableColumn>Status</TableColumn>
                    <TableColumn align="end">
                        <span className="sr-only">Actions</span>
                    </TableColumn>
                </TableHeader>
                <TableBody emptyContent="No flags match that search.">
                    {paged.items.map((entry) => {
                        const created = createdKeys?.has(entry.key) ?? false;
                        const canCreate =
                            !isLoadingKeys && !isKeysError && createdKeys !== null && !created;
                        return (
                            <TableRow key={entry.key}>
                                <TableCell className="max-w-[36rem]">
                                    <div className="flex min-w-0 flex-col gap-0.5">
                                        <span className="font-mono font-medium">{entry.key}</span>
                                        <span className="text-tiny text-foreground-600">
                                            {entry.description}
                                        </span>
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <Chip size="sm" variant="flat">
                                        {ENFORCED_LABEL[entry.enforcedIn]}
                                    </Chip>
                                </TableCell>
                                <TableCell>
                                    <StatusChip
                                        created={created}
                                        isLoading={isLoadingKeys}
                                        isError={isKeysError}
                                    />
                                </TableCell>
                                <TableCell>
                                    <RowActions
                                        label={`flag ${entry.key}`}
                                        primary={
                                            canCreate ? (
                                                <RowButton onPress={() => onCreate(entry)}>
                                                    Create
                                                </RowButton>
                                            ) : null
                                        }
                                    />
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>

            {paged.totalPages > 1 ? (
                <div className="flex justify-center">
                    <Pagination
                        total={paged.totalPages}
                        page={paged.page}
                        onChange={setPage}
                        showControls
                        size="sm"
                    />
                </div>
            ) : null}
        </section>
    );
}

function StatusChip({
    created,
    isLoading,
    isError,
}: {
    created: boolean;
    isLoading: boolean;
    isError: boolean;
}) {
    if (isError) {
        return (
            <Chip size="sm" variant="flat" color="danger">
                Unknown
            </Chip>
        );
    }
    if (isLoading) {
        return (
            <Chip size="sm" variant="flat">
                Checking
            </Chip>
        );
    }
    return (
        <Chip size="sm" variant="flat" color={created ? 'success' : 'default'}>
            {created ? 'Created' : 'Not created'}
        </Chip>
    );
}
